/**
 * failureSignatures.js ✦ iter208
 * ─────────────────────────────────────────────────────────────
 * Operational failure intelligence — the canonical vocabulary
 * AND the passive detector primitives EventDrop uses to recognise
 * failure archetypes in the wild.
 *
 * Per Craig (iter208): "you are modelling venue chaos, guest
 * behaviour, hardware stress, mobile ecosystem instability —
 * not developer failures. Operator manually classifies for now."
 *
 * The vocabulary commits to real-world archetypes:
 *
 *   permission:denied        — user blocked camera/mic
 *   permission:obstructed    — NotReadableError (device in use)
 *   device:notfound          — no camera/mic on device
 *   light:underexposed       — dark venue
 *   light:overexposed        — clipping (windows behind subject)
 *   lens:obstructed          — finger / cloth over lens
 *   mic:silent               — room tone present, no peaks rise
 *   mic:interrupt            — track ended during cycle
 *   network:offline          — !navigator.onLine at upload moment
 *   thermal:throttling       — frame gap signature
 *   bluetooth:transition     — devicechange during recording
 *   safari:gc-collapse       — memory delta crosses ceiling
 *
 * Severity vocabulary — for operational prioritisation, NEVER
 * surfaced to guests:
 *
 *   minor    — degraded experience but capture succeeded
 *   major    — capture compromised
 *   critical — capture lost
 *
 * The passive detectors are grounded, explainable primitives.
 * No "AI detected sadness in the room" — only "the lens is
 * physically blocked". Groundedness is the trust contract.
 */

export const FAILURE_CLASSES = Object.freeze({
  PERMISSION_DENIED:      'permission:denied',
  PERMISSION_OBSTRUCTED:  'permission:obstructed',
  DEVICE_NOTFOUND:        'device:notfound',
  LIGHT_UNDEREXPOSED:     'light:underexposed',
  LIGHT_OVEREXPOSED:      'light:overexposed',
  LENS_OBSTRUCTED:        'lens:obstructed',
  MIC_SILENT:             'mic:silent',
  MIC_INTERRUPT:          'mic:interrupt',
  NETWORK_OFFLINE:        'network:offline',
  THERMAL_THROTTLING:     'thermal:throttling',
  BLUETOOTH_TRANSITION:   'bluetooth:transition',
  SAFARI_GC_COLLAPSE:     'safari:gc-collapse',
});

export const FAILURE_CLASS_LIST = Object.freeze(Object.values(FAILURE_CLASSES));

export const SEVERITY = Object.freeze({
  MINOR:    'minor',
  MAJOR:    'major',
  CRITICAL: 'critical',
});

export const SEVERITY_LIST = Object.freeze(Object.values(SEVERITY));

/**
 * Detector thresholds — kept as named constants so iter209+ can
 * tune them per-device or per-profile without grep'ing magic
 * numbers out of detector functions.
 */
export const DETECTOR_THRESHOLDS = Object.freeze({
  LUMINANCE_DARK_MEAN: 28,         // 0..255 — venue underexposed
  LUMINANCE_BRIGHT_CLIPPED_PCT: 18, // % of pixels with V ≥ 250
  LUMINANCE_DARK_PCT: 60,          // % of pixels with V ≤ 16
  LENS_OBSTRUCT_VARIANCE: 80,      // very flat frame
  LENS_OBSTRUCT_MEAN_MIN: 20,      // not pitch black
  LENS_OBSTRUCT_MEAN_MAX: 200,     // not a paper held over lens
  MIC_SILENT_PEAK_RATIO: 1.2,      // peak ≤ 1.2× roomTone for whole cycle
});

/**
 * detectLuminance — compute mean luminance, variance, clipped%,
 * dark% from a canvas frame. Single sampling pass; safe on any
 * resolution.
 *
 * Returns: { mean, variance, clippedPct, darkPct, samples }
 *
 * The frame is sampled on a coarse grid (every Nth pixel) so a
 * 1080p still finishes in well under a frame budget on mobile.
 */
export function detectLuminance(canvas, { sampleStride = 8 } = {}) {
  if (!canvas || !canvas.getContext) return null;
  const ctx = canvas.getContext('2d');
  const w = canvas.width;
  const h = canvas.height;
  if (!w || !h) return null;
  let img;
  try { img = ctx.getImageData(0, 0, w, h); }
  catch { return null; /* cross-origin frame — refuse to lie */ }
  const data = img.data;
  let n = 0;
  let sum = 0;
  let sumSq = 0;
  let clipped = 0;
  let dark = 0;
  for (let i = 0; i < data.length; i += 4 * sampleStride) {
    // Rec.601 luma — cheap, no gamma correction (we want the
    // sensor-side perception, not display).
    const L = 0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2];
    sum += L;
    sumSq += L * L;
    if (L >= 250) clipped += 1;
    if (L <= 16) dark += 1;
    n += 1;
  }
  if (!n) return null;
  const mean = sum / n;
  const variance = (sumSq / n) - (mean * mean);
  return {
    mean,
    variance,
    clippedPct: (clipped / n) * 100,
    darkPct: (dark / n) * 100,
    samples: n,
  };
}

/**
 * detectLensObstruction — finger / cloth / sticker over the lens
 * produces an unusually flat, mid-toned frame. We require BOTH a
 * very low variance AND a mid-range mean to avoid false positives
 * on legitimately uniform scenes (a blank white wall, a black-out
 * curtain, sky out a plane window).
 */
export function detectLensObstruction(luminance) {
  if (!luminance) return false;
  const { mean, variance } = luminance;
  return (
    variance < DETECTOR_THRESHOLDS.LENS_OBSTRUCT_VARIANCE
    && mean >= DETECTOR_THRESHOLDS.LENS_OBSTRUCT_MEAN_MIN
    && mean <= DETECTOR_THRESHOLDS.LENS_OBSTRUCT_MEAN_MAX
  );
}

export function detectUnderexposed(luminance) {
  if (!luminance) return false;
  return (
    luminance.mean <= DETECTOR_THRESHOLDS.LUMINANCE_DARK_MEAN
    || luminance.darkPct >= DETECTOR_THRESHOLDS.LUMINANCE_DARK_PCT
  );
}

export function detectOverexposed(luminance) {
  if (!luminance) return false;
  return luminance.clippedPct >= DETECTOR_THRESHOLDS.LUMINANCE_BRIGHT_CLIPPED_PCT;
}

/**
 * detectMicSilent — `rawRoomToneRMS` was measurable (so the mic
 * was open and the room had ambient signal) but no peak rose
 * meaningfully above it across the cycle. Distinguishes:
 *
 *   • muted mic       → peak ≈ 0
 *   • silent speaker  → peak ≈ rawRoomToneRMS (room tone only)
 *   • normal voice    → peak ≫ rawRoomToneRMS (this returns false)
 */
export function detectMicSilent({ voicePresenceMoments = [], rawRoomToneRMS = 0, peakLevelTrace = [] } = {}) {
  if (!rawRoomToneRMS) return false; // mic was never open
  if (voicePresenceMoments.length > 0) return false;
  if (!peakLevelTrace.length) return true;
  const maxPeak = Math.max(...peakLevelTrace);
  return maxPeak <= rawRoomToneRMS * DETECTOR_THRESHOLDS.MIC_SILENT_PEAK_RATIO;
}

/**
 * Build a current "signature" from passive detectors + telemetry
 * counters. Used both by `classifyFailure` and by the operator UI
 * to surface live detector readouts.
 *
 * Inputs are all optional — pass what you have. The signature is
 * a structured snapshot, NOT an interpretation.
 */
export function buildSignature({
  luminance = null,
  voicePresenceMoments = [],
  rawRoomToneRMS = 0,
  peakLevelTrace = [],
  counters = null,
  conditions = null,
  cameraStatus = null,
  audioStatus = null,
  errors = {},
} = {}) {
  return {
    luminance,
    flags: {
      underexposed: detectUnderexposed(luminance),
      overexposed: detectOverexposed(luminance),
      lensObstructed: detectLensObstruction(luminance),
      micSilent: detectMicSilent({ voicePresenceMoments, rawRoomToneRMS, peakLevelTrace }),
    },
    voiceMomentCount: voicePresenceMoments.length,
    rawRoomToneRMS,
    peakMax: peakLevelTrace.length ? Math.max(...peakLevelTrace) : 0,
    counters,
    conditions,
    cameraStatus,
    audioStatus,
    errors,
  };
}

/**
 * classifyFailure — score the current signature against the
 * KNOWN_CLASSIFIERS rules. Returns a ranked list of
 *
 *   [{ class, confidence, why }]
 *
 * Confidence is 0..1. `why` is a human-readable explanation so
 * the operator can verify the classification instead of trusting
 * a black box.
 *
 * Stays purely rule-based on purpose — Craig's iter208 directive:
 * "no opaque ML; remains explainable".
 */
const KNOWN_CLASSIFIERS = [
  {
    cls: FAILURE_CLASSES.PERMISSION_DENIED,
    score: (sig) =>
      (sig.cameraStatus === 'denied' || sig.audioStatus === 'denied') ? 1 : 0,
    why: 'pipeline status reports denied',
  },
  {
    cls: FAILURE_CLASSES.PERMISSION_OBSTRUCTED,
    score: (sig) => {
      const m = sig.errors?.cameraName === 'NotReadableError'
        || sig.errors?.audioName === 'NotReadableError'
        || /in use|NotReadable/i.test(sig.errors?.cameraMessage || '')
        || /in use|NotReadable/i.test(sig.errors?.audioMessage || '');
      return m ? 0.9 : 0;
    },
    why: 'NotReadableError surfaced — device claimed by another app',
  },
  {
    cls: FAILURE_CLASSES.DEVICE_NOTFOUND,
    score: (sig) => {
      const m = sig.errors?.cameraName === 'NotFoundError'
        || sig.errors?.audioName === 'NotFoundError';
      return m ? 0.95 : 0;
    },
    why: 'NotFoundError — no matching device',
  },
  {
    cls: FAILURE_CLASSES.LIGHT_UNDEREXPOSED,
    score: (sig) => sig.flags?.underexposed ? 0.85 : 0,
    why: 'mean luminance below dark threshold or darkPct elevated',
  },
  {
    cls: FAILURE_CLASSES.LIGHT_OVEREXPOSED,
    score: (sig) => sig.flags?.overexposed ? 0.85 : 0,
    why: 'clipped-pixel percentage above threshold',
  },
  {
    cls: FAILURE_CLASSES.LENS_OBSTRUCTED,
    score: (sig) => sig.flags?.lensObstructed ? 0.8 : 0,
    why: 'variance very low with mid-range mean luminance',
  },
  {
    cls: FAILURE_CLASSES.MIC_SILENT,
    score: (sig) => sig.flags?.micSilent ? 0.75 : 0,
    why: 'room tone present, no voice peaks rose above it',
  },
  {
    cls: FAILURE_CLASSES.MIC_INTERRUPT,
    score: (sig) =>
      (sig.counters?.trackEndedMic || 0) > 0 ? 0.9 : 0,
    why: 'mic track ended during cycle (track-ended counter > 0)',
  },
  {
    cls: FAILURE_CLASSES.NETWORK_OFFLINE,
    score: (sig) =>
      sig.conditions?.onLine === false ? 1 : 0,
    why: 'navigator.onLine reported false',
  },
  {
    cls: FAILURE_CLASSES.BLUETOOTH_TRANSITION,
    score: (sig) =>
      ((sig.counters?.deviceChangesMic || 0) + (sig.counters?.deviceChangesCamera || 0)) > 0 ? 0.85 : 0,
    why: 'devicechange event observed during recording',
  },
];

export function classifyFailure(signature) {
  if (!signature) return [];
  const scored = KNOWN_CLASSIFIERS
    .map((c) => ({
      class: c.cls,
      confidence: Math.max(0, Math.min(1, c.score(signature))),
      why: c.why,
    }))
    .filter((x) => x.confidence > 0)
    .sort((a, b) => b.confidence - a.confidence);
  return scored;
}

export default {
  FAILURE_CLASSES,
  FAILURE_CLASS_LIST,
  SEVERITY,
  SEVERITY_LIST,
  DETECTOR_THRESHOLDS,
  detectLuminance,
  detectLensObstruction,
  detectUnderexposed,
  detectOverexposed,
  detectMicSilent,
  buildSignature,
  classifyFailure,
};
